import Range from 'rc-slider'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { formatNumberWithThousands } from '../../../helpers/common'
import { useDebounce } from '../../../hooks/useDebounce'
import styles from './InputRange.module.scss'
import { InputRangeType, InputValues } from './InputRange.types'

const InputRange = ({
  rangeValue,
  name,
  onChange,
  classModifiers,
  slider = true,
  step = 10,
  startValues = [
    Number(rangeValue[0].replaceAll(' ', '')),
    Number(rangeValue[1].replaceAll(' ', '')),
  ],
  initialValue = false,
  project = null,
}: InputRangeType) => {
  const location = useLocation()
  const classList = classModifiers
    ? classModifiers.map((el: string) => `${styles['rangeFilter--' + el]}`)
    : []

  const [startRange, setStartRange] = useState<InputValues>({
    min: Number(rangeValue[0].replaceAll(' ', '')),
    max: Number(rangeValue[1].replaceAll(' ', '')),
  })

  const [inputValue, setInputValue] = useState<InputValues>({
    min: startValues
      ? startValues[0]
      : Number(rangeValue[0].replaceAll(' ', '')),
    max: startValues
      ? startValues[1]
      : Number(rangeValue[1].replaceAll(' ', '')),
  })

  const [inputMinValue, setInputMinValue] = useState<string>(
    startValues ? formatNumberWithThousands(startValues[0].toString()) : ''
  )

  const [inputMaxValue, setInputMaxValue] = useState<string>(
    startValues ? formatNumberWithThousands(startValues[1].toString()) : ''
  )

  const debounceValue = useDebounce<object>(inputValue, 500)

  const handleSlider = (values: number | number[]) => {
    if (Array.isArray(values)) {
      setInputValue({
        min: values[0],
        max: values[1],
      })

      setInputMinValue(formatNumberWithThousands(values[0].toString()))
      setInputMaxValue(formatNumberWithThousands(values[1].toString()))
    }
  }

  const stringToNumber = (value: string): number =>
    Number(value.replaceAll(' ', ''))

  const setValue = (value: number, type: string) => {
    setInputValue(prevState => ({ ...prevState, [type]: value }))
  }

  const setValueMinOrMax = (value: number | string, type: string): void => {
    if (type === 'min') {
      setInputMinValue(formatNumberWithThousands(value.toString()))
    } else {
      setInputMaxValue(formatNumberWithThousands(value.toString()))
    }
  }

  const handleBlurInnputs = (
    e: ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    let value = stringToNumber(e.target.value)

    if (value > startRange.max) {
      value = startRange.max
      setValueMinOrMax(value, type)
    } else if (value < startRange.min) {
      value = startRange.min
      setValueMinOrMax(value, type)
    } else if (
      type === 'min' &&
      value > stringToNumber(inputMaxValue) &&
      inputMaxValue.length
    ) {
      value = stringToNumber(inputMaxValue)
      setValueMinOrMax(value, type)
    } else if (
      type === 'max' &&
      value < stringToNumber(inputMinValue) &&
      inputMinValue.length
    ) {
      value = stringToNumber(inputMinValue)
      setValueMinOrMax(value, type)
    }

    setValue(value, type)
  }

  const handleChangeInput = (
    e: ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const value = stringToNumber(e.target.value)

    setValueMinOrMax(value, type)

    setValue(value, type)
  }

  useEffect(() => {
    onChange([inputValue.min, inputValue.max], name)
  }, [debounceValue])

  useEffect(() => {
    setStartRange({
      min: Number(rangeValue[0].replaceAll(' ', '')),
      max: Number(rangeValue[1].replaceAll(' ', '')),
    })

    if (startValues) {
      handleSlider(startValues)
    }
  }, [initialValue, location, project])

  return (
    <div className={`${styles.rangeFilter} ${classList?.join(' ')}`}>
      <div className={styles.rangeFilter__filds}>
        <div className={styles.rangeFilter__block}>
          <label>
            <span>от</span>
            <input
              onChange={e => handleChangeInput(e, 'min')}
              value={inputMinValue}
              onBlur={e => handleBlurInnputs(e, 'min')}
              placeholder={formatNumberWithThousands(startRange.min.toString())}
            />
          </label>
        </div>
        <div className={styles.rangeFilter__block}>
          <label>
            <span>до</span>
            <input
              onChange={e => handleChangeInput(e, 'max')}
              value={inputMaxValue}
              onBlur={e => handleBlurInnputs(e, 'max')}
              placeholder={formatNumberWithThousands(startRange.max.toString())}
            />
          </label>
        </div>
      </div>
      {slider && (
        <div className={styles.rangeFilter__slider}>
          <Range
            allowCross={false}
            range
            step={step}
            defaultValue={[startRange.min, startRange.max] as number[]}
            value={[inputValue.min, inputValue.max]}
            min={startRange.min}
            max={startRange.max}
            onChange={handleSlider}
          />
        </div>
      )}
    </div>
  )
}

export default InputRange
